import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, Gauge, FileSignature, Receipt,
  CreditCard, MessageSquare, BarChart3, Workflow, Settings, Zap, AlertTriangle, HelpCircle, Wrench, Building2, Activity, X, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/kunder", label: "Kunder", icon: Users, testid: "nav-kunder" },
  { to: "/malepunkter", label: "Målepunkter", icon: Gauge, testid: "nav-malepunkter" },
  { to: "/avtaler", label: "Avtaler", icon: FileSignature, testid: "nav-avtaler" },
  { to: "/fakturering", label: "Fakturering", icon: Receipt, testid: "nav-fakturering" },
  { to: "/betaling", label: "Betaling", icon: CreditCard, testid: "nav-betaling" },
  { to: "/inkasso", label: "Inkasso", icon: AlertTriangle, testid: "nav-inkasso" },
  { to: "/saker", label: "Saker", icon: MessageSquare, testid: "nav-saker" },
  { to: "/rapporter", label: "Rapporter", icon: BarChart3, testid: "nav-rapporter" },
  { to: "/elhub-data", label: "Elhub-data", icon: Activity, testid: "nav-elhub-data" },
  { to: "/elhub-mt1", label: "Elhub MT1-test", icon: ShieldCheck, testid: "nav-elhub-mt1" },
  { to: "/automatisering", label: "Automatisering", icon: Workflow, testid: "nav-automatisering" },
  { to: "/drift", label: "Drift", icon: Wrench, testid: "nav-drift" },
  { to: "/status", label: "Produksjonsstatus", icon: HelpCircle, testid: "nav-status" },
  { to: "/innstillinger", label: "Innstillinger", icon: Settings, testid: "nav-innstillinger" },
];

const superadminItems = [
  { to: "/superadmin/tenants", label: "Kraftselskaper", icon: Building2, testid: "nav-superadmin-tenants" },
];

export default function Sidebar({ mobileOpen = false, onCloseMobile }) {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  return (
    <aside
      className={`
        w-64 shrink-0 border-r border-zinc-900 bg-[#080808] flex flex-col
        fixed lg:static inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-out
        ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      data-testid="sidebar"
    >
      <div className="h-14 px-5 flex items-center gap-2.5 border-b border-zinc-900">
        <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[#e11d48] to-[#1e3a8a] grid place-items-center shrink-0">
          <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold tracking-tight text-zinc-50 leading-tight">R-Strøm</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 -mt-0.5">Kraftplattform</div>
        </div>
        {/* Close-knapp kun på mobil */}
        <button
          onClick={onCloseMobile}
          className="lg:hidden p-1.5 rounded hover:bg-zinc-900 text-zinc-400 hover:text-zinc-100"
          data-testid="sidebar-close-mobile"
          aria-label="Lukk meny"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <nav className="flex-1 py-3 px-2 overflow-y-auto overscroll-contain">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            className={({ isActive }) =>
              [
                "relative flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 mb-0.5",
                isActive
                  ? "bg-zinc-900/60 text-emerald-400 nav-active"
                  : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100",
              ].join(" ")
            }
          >
            <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            <span>{it.label}</span>
          </NavLink>
        ))}
        {isSuperadmin && (
          <>
            <div className="mt-4 mb-1 px-3 text-[9px] uppercase tracking-widest text-zinc-600 font-medium">Plattform</div>
            {superadminItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={({ isActive }) =>
                  [
                    "relative flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 mb-0.5",
                    isActive
                      ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 border border-transparent",
                  ].join(" ")
                }
              >
                <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="px-4 py-3 border-t border-zinc-900 text-[10px] text-zinc-600 uppercase tracking-widest">
        v1.0 · CIS-plattform
      </div>
    </aside>
  );
}
